import { ActionManager, Camera, Color4, Engine, EventState, FileTools, IScreenshotSize, Observable, PickingInfo, Scene, Tools, TransformNode, Vector3 } from '@babylonjs/core';
import { Flat2DCamera, ICamera, Sphere360Camera } from 'Framework/Components/Controls/XView/Cameras';
import { IConfiguration, ICoordinates, IIconProvider, RenderType, isFlatConfiguration, isPanoramaConfiguration } from 'Framework/Components/Controls/XView/Core';
import { BaseHotspotOverlay, GuiOverlay, Hotspot360Overlay, HotspotFlat2DOverlay } from 'Framework/Components/Controls/XView/Overlays';
import { Cube360Renderer, Sphere360Renderer } from 'Framework/Components/Controls/XView/Renderers';
import { Flat2DRenderer } from 'Framework/Components/Controls/XView/Renderers/Flat2DRenderer';
import { IRenderer } from 'Framework/Components/Controls/XView/Renderers/IRenderer';
import { ContextMenu } from 'Framework/Components/Controls/XView/XView.ContextMenu';
import { UIElement } from 'Framework/Components/UIElement';

export class Viewer360 extends UIElement {
    public constructor(canvasElement: HTMLCanvasElement, iconProvider: IIconProvider, onCameraChangeCallback: (eventData: Sphere360Camera, eventState: EventState) => void = null) {
        super(...arguments);

        FileTools.CorsBehavior = 'use-credentials';

        this._canvasElement = canvasElement;
        this._engine = new Engine(this._canvasElement);
        this._mainScene = new Scene(this._engine);
        this._mainScene.clearColor = new Color4(0, 0, 0, 0);

        let camera = new Sphere360Camera('Camera', this._mainScene);
        camera.attachControl(false);
        this._camera = camera;

        this._uiScene = new GuiOverlay(this._engine);
        this._uiScene.activeCamera = camera;
        this._engine.displayLoadingUI();

        this._iconProvider = iconProvider;
        this._onCameraChangeCallback = onCameraChangeCallback;

        this._mainScene.actionManager = new ActionManager(this._mainScene);

        this._contextMenu = new ContextMenu(
            'contextMenu',
            this._uiScene as GuiOverlay,
            {
                fontSize: 12,
                fontFamily: 'Tahoma, Arial, sans-serif'
            }
        );

        this.isMobile = this.viewerWidth < 600;

        // the canvas/window resize event handler
        window.addEventListener('resize', this.resizeHandler);

        UIElement.Bus.$on('look-to-event-bus', this._lookToEventAction);
        UIElement.Bus.$on('set-fov', this._setFovAction);
        UIElement.Bus.$on('refresh-hotspots', this._refreshHotspotsAction);
    }

    public beforeDestroy(): void {
        window.removeEventListener('resize', this.resizeHandler);

        UIElement.Bus.$off('look-to-event-bus', this._lookToEventAction);
        UIElement.Bus.$off('set-fov', this._setFovAction);
        UIElement.Bus.$off('refresh-hotspots', this._refreshHotspotsAction);

        this._engine.dispose();
    }

    public addNewScene(): Scene {
        const scene = new Scene(this._engine);
        scene.autoClear = false;
        scene.addTransformNode(new TransformNode('root'));

        return scene;
    }

    /**
     * Defines the target the camera should look at,
     * @param position Cartesian coordinates, if null or undefined a zero value will be apply.
     */
    public lookTo(position: Vector3 | ICoordinates): void {
        this._camera.lookTo(position);
    }

    public takeScreenshotAsync(): Promise<string> {
        const size: IScreenshotSize = this._canvasElement;

        return Tools.CreateScreenshotUsingRenderTargetAsync(this._engine, this.camera, size);
    }

    public async loadConfigurationAsync(configuration: IConfiguration): Promise<void> {
        if (this._configuration) {
            //remove all scene except the main and GuiOverlay
            while (this._engine.scenes.length > 2) {
                const scene = this._engine.scenes[2];
                scene.dispose();
            }
        }

        UIElement.Bus.$emit('preview-loaded');

        this._configuration = configuration;

        if (isPanoramaConfiguration(configuration)) {
            this._internalRenderer = configuration.RenderType == RenderType.Cube
                ? new Cube360Renderer(this._engine, this._camera)
                : new Sphere360Renderer(this._engine, this._camera);

            this._internalRenderer.load(configuration);

            this._hotspotRenderer = new Hotspot360Overlay(
                this._engine,
                this,
                this._camera,
                255,
                this._iconProvider
            );
        }
        else if (isFlatConfiguration(configuration)) {
            this.camera = new Flat2DCamera('Camera', this._mainScene);
            this.camera.attachControl(false);

            this._internalRenderer = new Flat2DRenderer(this._engine, this._camera)
                .load(configuration)
                .fit();

            this._hotspotRenderer = new HotspotFlat2DOverlay(
                this._engine,
                this,
                this._camera,
                configuration.ratio,
                this._iconProvider
            );
        }

        await this._hotspotRenderer.loadHotspotsAsync(configuration.hotspots);

        if (this._onCameraChangeCallback) {
            this.camera.onViewMatrixChangedObservable.clear();
            this.camera.onProjectionMatrixChangedObservable.clear();

            this.camera.onViewMatrixChangedObservable.add(this._onCameraChangeCallback);
            this.camera.onProjectionMatrixChangedObservable.add(this._onCameraChangeCallback);
        }

        this.startRendering();

        UIElement.Bus.$emit('configuration-changed', { sender: this, args: configuration });

        this._engine.hideLoadingUI();
    }

    public stopRendering(): void {
        this._engine.stopRenderLoop();
    }

    /**
     *  run the render loop
     */
    public startRendering(): void {
        this._engine.runRenderLoop(() => {
            if (!this._mainScene.isDisposed && this._mainScene.activeCamera != null) {
                this._mainScene.render();

                for (const scene of this._engine.scenes) {
                    if (scene != this._mainScene
                        && scene != this._uiScene) {
                        scene.render();
                    }
                }

                if (this._uiScene.activeCamera) {
                    this._uiScene.render();
                }
            }
        });
    }

    public resizeView(): void {
        this._engine.resize();
    }

    public setSizeView(width: number, height: number): void {
        this._engine.setSize(width, height);
    }

    public pick(x: number, y: number): PickingInfo {
        return this._internalRenderer?.pick(x, y);
    }

    private _refreshHotspots(): void {
        this._hotspotRenderer.clear();
        const _ = this._hotspotRenderer.loadHotspotsAsync(this._configuration.hotspots);
    }

    private _setFov(fov: number): void {
        this.camera.fov = fov;
    }

    public get FPS(): number {
        return this._engine.getFps();
    }

    public get camera(): Camera & ICamera {
        return this._camera;
    }

    private set camera(value: Camera & ICamera) {
        this._camera = value;
    }

    public get contextMenu(): ContextMenu {
        return this._contextMenu;
    }

    public get viewerWidth(): number {
        return Math.max(
            document.documentElement.clientWidth || 0,
            window.innerWidth || 0
        );
    }

    public get isMobile(): boolean {
        return this._isMobile;
    }

    public set isMobile(value: boolean) {
        if (this._isMobile != value) {
            this._isMobile = value;
            this.onMobileObservable.notifyObservers(value)
        }
    }

    public get scenes(): Array<Scene> {
        return this._engine.scenes;
    }

    public readonly resizeHandler = (_e: UIEvent) => {
        this.isMobile = this.viewerWidth < 600;
        this._engine.resize();
    }

    public onMobileObservable: Observable<boolean> = new Observable<boolean>();
    public autoRotationEnabled: boolean;
    public isCameraMoving: boolean;

    private readonly _contextMenu: ContextMenu;
    private readonly _canvasElement: HTMLCanvasElement;
    private readonly _engine: Engine;
    private readonly _mainScene: Scene;
    private readonly _uiScene: Scene;
    private readonly _iconProvider: IIconProvider;
    private readonly _lookToEventAction: (sender: any, e: Vector3) => void = (o, e) => this.lookTo(e);
    private readonly _setFovAction: (e: { sender: any, fov: number }) => void = (e) => this._setFov(e.fov);
    private readonly _refreshHotspotsAction: () => void = () => this._refreshHotspots();
    private readonly _onCameraChangeCallback: (eventData: Sphere360Camera, eventState: EventState) => void;

    private _hotspotRenderer: BaseHotspotOverlay;
    private _configuration: IConfiguration;
    private _internalRenderer: IRenderer;
    private _isMobile: boolean;
    private _camera: Camera & ICamera;
}
